/**
 * Re-export a .glb / .obj model so that 1 unit of the returned file equals 1 meter.
 *
 * Strategy (safe against any downstream consumer):
 *
 *   GLB — bake the uniform scale into the **binary vertex data** itself:
 *     1. Every node's `translation` (or the translation column of `matrix`) is
 *        multiplied by `scale`, so every mesh's anchor point in world space
 *        lands at the correct real-world position.
 *     2. Every unique POSITION accessor (and its morph-target POSITION deltas)
 *        has its float values multiplied by `scale` in the BIN chunk, and
 *        accessor `min`/`max` bounds are rescaled.
 *     3. Node `rotation` values and local `scale` fields are **untouched** —
 *        uniform scaling commutes with rotation, and we absorb the magnitude
 *        into geometry + translations instead.
 *
 *   Why this over a wrapper-scale-node approach: tools that read POSITION
 *   accessors directly (auto-positioning, bounding boxes, raycasters, custom
 *   parsers) never walk the scene graph and therefore would ignore any
 *   transform on a wrapper node. Baking makes the scale visible no matter how
 *   the file is consumed — a lamp placed at vertex `(1,0,0)` in the uploaded
 *   GLB will land exactly where the visible vertex now sits.
 *
 *   Preserved: mesh/node names, primitive ordering, accessor indices,
 *   bufferView indices, material/texture references, extensions, JSON shape.
 *
 *   OBJ — regex-replace `v x y z` lines. Normals (`vn`), UVs (`vt`), faces (`f`),
 *   groups, materials all untouched.
 */
export async function rescaleModelBlob(url: string, filename: string, scale: number): Promise<Blob> {
  const ext = filename.split('.').pop()?.toLowerCase();

  if (ext === 'glb') {
    return rescaleGlbBinary(url, scale);
  }
  if (ext === 'obj') {
    return rescaleObjText(url, scale);
  }
  throw new Error(`Unsupported model extension for rescale: ${ext}`);
}

// ─────────────────────────────────────────────────────────────────────────────
// GLB: parse header, bake scale into POSITION accessors + translations
// ─────────────────────────────────────────────────────────────────────────────

const GLB_MAGIC = 0x46546c67; // 'glTF'
const CHUNK_JSON = 0x4e4f534a; // 'JSON'
const CHUNK_BIN = 0x004e4942; // 'BIN\0'
const COMPONENT_TYPE_FLOAT = 5126;

async function rescaleGlbBinary(url: string, scale: number): Promise<Blob> {
  const resp = await fetch(url);
  const buf = await resp.arrayBuffer();
  const view = new DataView(buf);

  if (view.getUint32(0, true) !== GLB_MAGIC) throw new Error('Not a GLB file');
  if (view.getUint32(4, true) !== 2) throw new Error('Unsupported GLB version');

  // First chunk must be JSON
  const jsonChunkLen = view.getUint32(12, true);
  if (view.getUint32(16, true) !== CHUNK_JSON) throw new Error('First GLB chunk is not JSON');
  const jsonStart = 20;
  const jsonEnd = jsonStart + jsonChunkLen;
  const jsonText = new TextDecoder().decode(new Uint8Array(buf, jsonStart, jsonChunkLen));
  const json = JSON.parse(jsonText) as GltfJson;

  // Optional BIN chunk — clone it so we can mutate vertex data without
  // touching the source ArrayBuffer.
  let binData: Uint8Array | null = null;
  if (jsonEnd < buf.byteLength) {
    const binChunkLen = view.getUint32(jsonEnd, true);
    const binChunkType = view.getUint32(jsonEnd + 4, true);
    if (binChunkType === CHUNK_BIN) {
      binData = new Uint8Array(new ArrayBuffer(binChunkLen));
      binData.set(new Uint8Array(buf, jsonEnd + 8, binChunkLen));
    }
  }

  // ── 1) Scale every node's translation ───────────────────────────────────
  if (json.nodes) {
    for (const node of json.nodes) {
      if (node.translation) {
        node.translation = [node.translation[0] * scale, node.translation[1] * scale, node.translation[2] * scale];
      } else if (node.matrix && node.matrix.length === 16) {
        // Column-major 4x4 — translation column is at indices 12, 13, 14
        const m = [...node.matrix];
        m[12] *= scale;
        m[13] *= scale;
        m[14] *= scale;
        node.matrix = m;
      }
    }
  }

  // ── 2) Scale every unique POSITION accessor (+ morph-target POSITIONs) ──
  const scaledAccessors = new Set<number>();
  const scaleAccessor = (accessorIdx: number | undefined) => {
    if (typeof accessorIdx !== 'number') return;
    if (scaledAccessors.has(accessorIdx)) return;
    scaledAccessors.add(accessorIdx);
    const accessor = json.accessors?.[accessorIdx];
    if (!accessor) return;

    // Always rescale min/max bounds if present
    if (accessor.min) accessor.min = accessor.min.map((v: number) => v * scale);
    if (accessor.max) accessor.max = accessor.max.map((v: number) => v * scale);

    // Rescale actual float values in the BIN chunk
    if (!binData) return;
    if (accessor.type !== 'VEC3') return;
    if (accessor.componentType !== COMPONENT_TYPE_FLOAT) {
      // Quantized positions (e.g. KHR_mesh_quantization) would need extension-
      // aware handling; we haven't encountered them yet. Fall back to min/max.
      console.warn(
        `rescaleModel: POSITION accessor #${accessorIdx} is not FLOAT (componentType=${accessor.componentType}); ` +
          `scaled min/max only, raw vertex data left untouched`,
      );
      return;
    }

    const bvIdx = accessor.bufferView;
    if (typeof bvIdx !== 'number') return;
    const bv = json.bufferViews?.[bvIdx];
    if (!bv) return;

    const byteOffsetInBin = (bv.byteOffset ?? 0) + (accessor.byteOffset ?? 0);
    const stride = bv.byteStride ?? 12; // 12 = float VEC3 tightly packed
    const count = accessor.count;

    const dv = new DataView(binData.buffer, binData.byteOffset, binData.byteLength);
    for (let i = 0; i < count; i++) {
      const off = byteOffsetInBin + i * stride;
      const x = dv.getFloat32(off, true);
      const y = dv.getFloat32(off + 4, true);
      const z = dv.getFloat32(off + 8, true);
      dv.setFloat32(off, x * scale, true);
      dv.setFloat32(off + 4, y * scale, true);
      dv.setFloat32(off + 8, z * scale, true);
    }
  };

  if (json.meshes) {
    for (const mesh of json.meshes) {
      for (const prim of mesh.primitives ?? []) {
        scaleAccessor(prim.attributes?.POSITION);
        if (prim.targets) {
          for (const target of prim.targets) {
            scaleAccessor(target?.POSITION);
          }
        }
      }
    }
  }

  // ── 3) Reassemble the GLB container ─────────────────────────────────────
  const newJsonBytes = encodeJsonChunk(json);
  const newBinBytes = binData ? padTo4(binData, 0x00) : null;

  const headerLen = 12;
  const jsonHeaderLen = 8;
  const binHeaderLen = newBinBytes ? 8 : 0;
  const totalLen = headerLen + jsonHeaderLen + newJsonBytes.length + binHeaderLen + (newBinBytes?.length ?? 0);

  const out = new ArrayBuffer(totalLen);
  const outView = new DataView(out);
  const outBytes = new Uint8Array(out);

  outView.setUint32(0, GLB_MAGIC, true);
  outView.setUint32(4, 2, true);
  outView.setUint32(8, totalLen, true);

  outView.setUint32(12, newJsonBytes.length, true);
  outView.setUint32(16, CHUNK_JSON, true);
  outBytes.set(newJsonBytes, 20);

  if (newBinBytes) {
    const binOffset = 20 + newJsonBytes.length;
    outView.setUint32(binOffset, newBinBytes.length, true);
    outView.setUint32(binOffset + 4, CHUNK_BIN, true);
    outBytes.set(newBinBytes, binOffset + 8);
  }

  return new Blob([out], { type: 'model/gltf-binary' });
}

function encodeJsonChunk(json: unknown): Uint8Array {
  let text = JSON.stringify(json);
  while (text.length % 4 !== 0) text += ' ';
  return new TextEncoder().encode(text);
}

function padTo4(src: Uint8Array, fill: number): Uint8Array {
  const pad = (4 - (src.length % 4)) % 4;
  if (pad === 0) return src;
  const out = new Uint8Array(src.length + pad);
  out.set(src, 0);
  for (let i = 0; i < pad; i++) out[src.length + i] = fill;
  return out;
}

// Minimal typing for the parts of glTF JSON we touch
interface GltfJson {
  scene?: number;
  scenes?: Array<{ nodes?: number[]; name?: string }>;
  nodes?: Array<{
    name?: string;
    children?: number[];
    translation?: [number, number, number];
    rotation?: [number, number, number, number];
    scale?: [number, number, number];
    matrix?: number[];
    mesh?: number;
    [k: string]: unknown;
  }>;
  meshes?: Array<{
    name?: string;
    primitives?: Array<{
      attributes?: { POSITION?: number; [k: string]: number | undefined };
      indices?: number;
      material?: number;
      mode?: number;
      targets?: Array<{ POSITION?: number; [k: string]: number | undefined }>;
      [k: string]: unknown;
    }>;
    [k: string]: unknown;
  }>;
  accessors?: Array<{
    bufferView?: number;
    byteOffset?: number;
    componentType: number;
    count: number;
    type: string;
    min?: number[];
    max?: number[];
    [k: string]: unknown;
  }>;
  bufferViews?: Array<{
    buffer: number;
    byteOffset?: number;
    byteLength: number;
    byteStride?: number;
    target?: number;
  }>;
  [k: string]: unknown;
}

// ─────────────────────────────────────────────────────────────────────────────
// OBJ: regex edit of vertex lines only
// ─────────────────────────────────────────────────────────────────────────────

async function rescaleObjText(url: string, scale: number): Promise<Blob> {
  const resp = await fetch(url);
  const text = await resp.text();

  // Split on either line ending and process line-by-line. A regex-only pass
  // is fragile: OBJ files in the wild use `.5` (no leading digit), include
  // an optional `w` homogeneous coordinate, carry trailing vertex colors,
  // and may be LF or CRLF.
  const useCrlf = text.includes('\r\n');
  const lines = text.split(/\r?\n/);
  const out: string[] = new Array(lines.length);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Only the `v` keyword (vertex position) — NOT `vn`, `vt`, `vp`.
    // Preserve any leading whitespace in case the file indents.
    const match = line.match(/^(\s*)v[ \t]+(.*)$/);
    if (!match) {
      out[i] = line;
      continue;
    }
    const indent = match[1];
    const tokens = match[2].trim().split(/\s+/);
    if (tokens.length < 3) {
      out[i] = line;
      continue;
    }
    const nx = parseFloat(tokens[0]);
    const ny = parseFloat(tokens[1]);
    const nz = parseFloat(tokens[2]);
    if (isNaN(nx) || isNaN(ny) || isNaN(nz)) {
      out[i] = line;
      continue;
    }
    // Tokens past index 2 may be the `w` coord (always 1 in practice) or
    // per-vertex RGB colors supported by some OBJ dialects. Pass them
    // through untouched — scaling them would be wrong.
    const extras = tokens.slice(3);
    const scaled = `${nx * scale} ${ny * scale} ${nz * scale}`;
    out[i] = `${indent}v ${scaled}${extras.length ? ' ' + extras.join(' ') : ''}`;
  }

  // Emit a blob with NO MIME type — the caller in ProjectSettings falls back
  // to the file's original content type, so the backend keeps routing this
  // as a plain `.obj` upload rather than as `text/plain`.
  return new Blob([out.join(useCrlf ? '\r\n' : '\n')]);
}
